<template>
  <v-container fluid>
    <v-row class="d-flex flex-row">
      <v-col cols="12">
        <FormGuide
          :data="guide"
          :audioguides="audioguides"
          :cities="cities"
          :routes="routes"
          :shared-access="sharedAccess"
          :ticket-reservations="ticketReservations"
          :loading="isFormLoading"
          :disabled="isMutationLoading"
          :isNew="!id"
          @submit="formGuideSubmit"
          @delete-guide="deleteGuide"
          :language="language"
          @language-update="language = $event"
          @change:descriptionForMobile="changeDescForMobHandler($event)"
        />
      </v-col>
    </v-row>
    <ConfirmationDialog ref="confirmGuideRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import FormGuide from './form'
import ConfirmationDialog from '@/components/confirmation-dialog'
import guideQuery from '@/services/gql/types/Guides/queries/guide.gql'
import routesQuery from '@/services/gql/types/Routes/queries/routesGrid.gql'
import citiesQuery from '@/services/gql/types/City/queries/citiesGrid.gql'
import audioguidesQuery from '@/services/gql/types/Audioguides/queries/audioguidesGrid.gql'
import sharedAccessQuery from '@/services/gql/types/SharedAccess/queries/sharedAccess.gql'
import ticketReservationsOfRelations from '@/services/gql/types/TicketReservations/queries/ticketReservationsOfRelations.gql'
import createGuide from '@/services/gql/types/Guides/mutations/createGuide.gql'
import deleteGuide from '@/services/gql/types/Guides/mutations/deleteGuide.gql'
import updateGuide from '@/services/gql/types/Guides/mutations/updateGuide.gql'
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'

export default {
  name: 'PageGuide',
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    FormGuide,
    ConfirmationDialog
  },
  metaInfo () {
    return {
      title: this.id ? 'Редактировать гида' : 'Создать гида'
    }
  },
  data () {
    return {
      language: contentLocales[0].value,
      guide: {},
      routes: null,
      audioguides: null,
      blogCategories: null,
      cities: null,
      sharedAccess: null,
      ticketReservations: null
    }
  },
  computed: {
    isFormLoading () {
      return (
        this.$apollo.queries.guide.loading ||
        this.$apollo.queries.routes.loading ||
        this.$apollo.queries.audioguides.loading ||
        this.$apollo.queries.cities.loading
      )
    },
    isMutationLoading () {
      return this.$async.createGuide.$pending || this.$async.updateGuide.$pending
    }
  },
  methods: {
    changeDescForMobHandler ({ fields, $event }) {
      fields.descriptionForMobile = $event
    },
    async formGuideSubmit (guidePayload) {
      if (!this.id) {
        await this.createGuide(guidePayload)
      } else {
        await this.updateGuide(guidePayload)
      }
    },
    async deleteGuide () {
      try {
        await this.$refs.confirmGuideRemoving.open()
        await this.$async.deleteGuide.$perform({ id: Number(this.id) })
        this.$successMsg('Гид удален')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении гида с ID ${this.id}`)
      } finally {
        await this.$router.push({ name: 'page-guides' })
      }
    },
    async updateGuide (data) {
      try {
        setTranslation(data, this.language)
        await this.$async.updateGuide.$perform({
          id: Number(this.id),
          data
        }, { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) })
        this.$successMsg('Гид изменен')
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при редактировании гида')
      }
    },
    async createGuide (data) {
      try {
        delete data.translations
        await this.$async.createGuide.$perform({
          data
        })
        this.$successMsg('Гид создан')
        await this.$router.push({ name: 'page-guides' })
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании гида')
      }
    }
  },
  apollo: {
    guide: {
      query: guideQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            id: Number(this.id)
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-guides' })
        } else {
          this.guide = {
            ...res?.data?.guide,
            cityId: res?.data?.guide?.city?.id || null,
            imageId: res?.data?.guide?.image?.id || null
          }
        }
      }
    },
    routes: {
      query: routesQuery,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables: {
        where: {}
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-guides' })
        } else {
          this.routes =
            res?.data?.routes?.list.map(route => ({
              id: route.id,
              title: route.title
            })) || null
        }
      }
    },
    audioguides: {
      query: audioguidesQuery,
      fetchPolicy: 'network-only',
      pollInterval: 30000,
      variables: {
        where: {}
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-guides' })
        } else {
          this.audioguides =
            res?.data?.audioguides?.list.map(audioguide => ({
              id: audioguide.id,
              title: audioguide.title
            })) || null
        }
      }
    },
    cities: {
      query: citiesQuery,
      fetchPolicy: 30000,
      variables: {
        where: {
          status_not: 'DRAFT'
        }
      },
      result (res) {
        if (res.data === undefined) {
          this.$handleApiError(res.error)
          this.$router.push({ name: 'page-guides' })
        } else {
          this.cities =
            res?.data?.cities?.list.map(city => ({
              id: city.id,
              title: city.title
            })) || null
        }
      }
    },
    sharedAccess: {
      query: sharedAccessQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profile: {
              id: Number(this.id)
            }
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        this.sharedAccess = res?.data?.sharedAccess?.list
      }
    },
    ticketReservations: {
      query: ticketReservationsOfRelations,
      fetchPolicy: 'network-only',
      skip () {
        return !this.id
      },
      variables () {
        return {
          where: {
            profileType: 'GUIDE',
            profileId: Number(this.id)
          }
        }
      },
      context () { return { headers: { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) } } },
      result (res) {
        const { list } = res?.data?.ticketReservations
        const tickets = list
          ?.map(ticket => ({ count: 1, name: ticket.displayName }))
          ?.reduce((acc, ticket) => {
            acc[ticket.name] = (acc[ticket.name] || 0) + ticket.count
            return acc
          }, {}) || null
        this.ticketReservations = Object.keys(tickets)?.map(ticket => ({
          title: ticket + ': ' + tickets[ticket]
        }))
      }
    }
  },
  async: {
    createGuide (variables) {
      return this.$apollo.mutate({
        mutation: createGuide,
        variables
      })
    },
    deleteGuide (variables) {
      return this.$apollo.mutate({
        mutation: deleteGuide,
        variables
      })
    },
    updateGuide (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateGuide,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
